import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

export const COMPANY_FILTERS = [
  { value: "all", label: "All companies" },
  { value: "overdue", label: "Overdue invoices" },
  { value: "attention", label: "Billing needs attention" },
  { value: "active", label: "Active locations" },
  { value: "setting_up", label: "Setting up" },
  { value: "inactive", label: "Inactive" },
  { value: "no_locations", label: "No locations" },
  { value: "deletion_requested", label: "Deletion requested" },
];

export const COMPANY_STATUS_LABELS = {
  active: "Active locations", setting_up: "Setting up", inactive: "Inactive",
  no_locations: "No locations", deletion_requested: "Deletion requested",
};

export function filterCompanies(companies, search, filter) {
  const term = search.trim().toLowerCase();
  return companies.filter((company) => {
    const text = [company.company_name, company.company_id,
      company.billing_settings?.accounting_primary_email,
      company.billing_settings?.stripe_customer_id,
      ...(company.people?.admins || []).flatMap((admin) => [admin.name, admin.email]),
    ].filter(Boolean).join(" ").toLowerCase();
    const matchesFilter = filter === "all" || company.company_status === filter
      || (filter === "overdue" && company.payment_health?.overdue_invoice_count > 0)
      || (filter === "attention" && (company.payment_health?.health === "needs_attention" || company.needs_invoice || company.missing_rate_store_count > 0
        || (company.billable_store_count > 0 && !company.stripe_connected)));
    return text.includes(term) && matchesFilter;
  });
}

const inputClass = "mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-600";
const secondaryClass = "rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50";

export function CompanyPeopleDetails({ company, onRequestDeletion, onCancelDeletion, onApproveDeletion, saving }) {
  const pending = company.deletion_request?.status === "requested";
  const people = company.people || {};
  return <div className="mb-5 space-y-4">
    <div className="grid grid-cols-3 gap-4 lg:grid-cols-1">
      <section className="rounded-lg border border-slate-200 p-4">
        <h4 className="text-sm font-semibold text-slate-900">Team adoption</h4>
        <p className="mt-2 text-2xl font-semibold text-slate-900">{people.member_count || 0} <span className="text-sm font-normal text-slate-500">team members</span></p>
        <p className="mt-1 text-sm text-slate-600">{people.signed_in_member_count || 0} have signed in to Rightwork</p>
        <p className="mt-2 text-xs text-slate-500">Current memberships, excluding internal staff. Sign-in is account-wide, not activity at this company. Unlinked team records count separately.</p>
      </section>
      <section className="rounded-lg border border-slate-200 p-4">
        <h4 className="text-sm font-semibold text-slate-900">Company admins</h4>
        <div className="mt-2 max-h-44 space-y-3 overflow-y-auto">
          {(people.admins || []).map((admin, index) => <div key={`${admin.email}-${index}`} className="text-sm">
            <p className="font-medium text-slate-800">{admin.name || "Unnamed admin"}</p>
            <p className="break-all text-slate-600">{admin.email || "No email recorded"}</p>
            <p className="text-xs text-slate-500">{admin.has_signed_in ? "Has signed in" : "No sign-in recorded"}</p>
          </div>)}
          {!people.admins?.length && <p className="text-sm text-slate-500">No current company admins.</p>}
        </div>
      </section>
      <section className="rounded-lg border border-slate-200 p-4">
        <h4 className="text-sm font-semibold text-slate-900">Company origin</h4>
        {company.creation ? <div className="mt-2 space-y-1 text-sm text-slate-600">
          <p className="font-medium text-slate-800">{company.creation.name}</p>
          <p className="break-all">{company.creation.email}</p>
          <p>{new Date(company.creation.created_at).toLocaleDateString()}</p>
          <p className="text-xs text-slate-500">Self-service signup</p>
        </div> : <p className="mt-2 text-sm text-slate-500">Creator was not recorded for this company.</p>}
        <p className="mt-3 text-xs text-slate-500">Company #{company.company_id} · {company.location_count ?? company.stores.length} locations</p>
      </section>
    </div>
    <section className="flex items-start justify-between gap-4 rounded-lg border border-rose-200 bg-rose-50 p-4 md:flex-col">
      <div>
        <h4 className="text-sm font-semibold text-rose-900">{pending ? "Deletion awaiting internal review" : "Company deletion"}</h4>
        {pending && <>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-rose-900">{company.deletion_request.reason}</p>
          <p className="mt-1 text-xs text-rose-800">Requested by {company.deletion_request.requested_by_email} on {new Date(company.deletion_request.requested_at).toLocaleString()}</p>
        </>}
        <p className="mt-2 max-w-2xl text-xs text-rose-800">Requests require impact review and a separate permanent-deletion approval. Access stays active until removal begins. Admin notices and a grace period can be selected during approval.</p>
      </div>
      {company.deletion_job ? <p className="shrink-0 text-sm font-medium text-rose-800">Deletion {company.deletion_job.status.replaceAll("_", " ")} · See deletion jobs above</p> : <div className="flex shrink-0 flex-wrap gap-2">
      {pending && <button type="button" disabled={saving} onClick={() => onApproveDeletion(company)} className="rounded-lg bg-rose-700 px-3 py-2 text-sm font-medium text-white hover:bg-rose-800 disabled:opacity-50">Review permanent deletion</button>}
      <button type="button" disabled={saving} onClick={() => pending ? onCancelDeletion(company) : onRequestDeletion(company)}
        className="shrink-0 rounded-lg border border-rose-300 bg-white px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50">
        {saving ? "Saving…" : pending ? "Cancel deletion request" : "Request deletion"}
      </button>
      </div>}
    </section>
  </div>;
}

export function DeletionRequestDialog({ company, onClose, onSubmit, saving }) {
  const [name, setName] = React.useState("");
  const [reason, setReason] = React.useState("");
  return <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="deletion-title">
    <DialogTitle id="deletion-title">Request company deletion</DialogTitle>
    <DialogContent>
      <p className="text-sm text-slate-600">Request internal review of <strong>{company.company_name}</strong>, including {company.location_count ?? company.stores.length} locations and {company.people?.member_count || 0} current team members.</p>
      <p className="mt-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">Permanent deletion removes company and location data, files, chat channels, and integration credentials, and cancels linked Stripe subscriptions. Shared user accounts are retained. This request does not delete data or notify admins.</p>
      <label className="mt-4 block text-sm font-medium text-slate-700">Reason for deletion
        <textarea autoFocus rows={3} maxLength={1000} value={reason} disabled={saving} onChange={(event) => setReason(event.target.value)} className={inputClass} />
      </label>
      <label className="mt-4 block text-sm font-medium text-slate-700">Type {company.company_name} to confirm
        <input value={name} disabled={saving} onChange={(event) => setName(event.target.value)} autoComplete="off" className={inputClass} />
      </label>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3 }}>
      <button type="button" disabled={saving} onClick={onClose} className={secondaryClass}>Cancel</button>
      <button type="button" disabled={saving || name !== company.company_name || reason.trim().length < 5}
        onClick={() => onSubmit({ company_id: company.company_id, action: "request", confirmed_name: name, reason })}
        className="rounded-lg bg-rose-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-800 disabled:opacity-40">{saving ? "Saving…" : "Submit deletion request"}</button>
    </DialogActions>
  </Dialog>;
}

export function StripeSetupDialog({ company, onClose, onSubmit, saving }) {
  const [email, setEmail] = React.useState(company.billing_settings?.accounting_primary_email || "");
  return <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="stripe-setup-title">
    <form onSubmit={(event) => { event.preventDefault(); onSubmit(company, email.trim()); }}>
      <DialogTitle id="stripe-setup-title">Set up Stripe customer</DialogTitle>
      <DialogContent>
        <p className="text-sm text-slate-600">Create a Stripe customer for {company.company_name}. This saves the billing email below; it does not create a subscription or charge the customer.</p>
        <label className="mt-4 block text-sm font-medium text-slate-700">Primary billing email
          <input autoFocus type="email" required value={email} disabled={saving} onChange={(event) => setEmail(event.target.value)} className={inputClass} />
        </label>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <button type="button" disabled={saving} onClick={onClose} className={secondaryClass}>Cancel</button>
        <button type="submit" disabled={saving || !email.trim()} className="rounded-lg bg-violet-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-800 disabled:opacity-40">{saving ? "Creating…" : "Create customer"}</button>
      </DialogActions>
    </form>
  </Dialog>;
}
